import { gql, useMutation, useQuery, useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Image } from 'native-base';
import { useIsMobile } from '../../Common/hooks/useIsMobile';
import { pusherSubscribeObject } from '../../Common/utils/pusher';
import { Icon } from '../../Common/components/Icon';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { useUserMessageNames } from '../../Common/hooks/useUserMessageNames';
import { faUserCircle } from '@fortawesome/pro-solid-svg-icons/faUserCircle';
import { isDoctorPortal } from '../../Common/utils/isDoctorPortal';
import { unreadMessageCountQuery } from './useInboxMessagesCount';
import { brandFromCustomerID } from '../../Common/utils/brandFromCustomerID';

export const pagedMessageForCustomerQuery = gql`
  query pagedMessages($limit: Int, $before: AWSDateTime) {
    getMessagesPaged(limit: $limit, before: $before) {
      __typename
      id
      unreadDoctor
      unreadCustomer
      identityChannel
      channel
      assignedUser
      open
      brand
      hasMore
      customer {
        given
        mobile
        surname
        id
        name
        __typename
      }
      messages {
        __typename
        html
        title
        files
        id
        sent
        createdAt
        senderID
        message
        final
        channel
        photo
        type
        options
      }
    }
  }
`;

export const pagedMessageForDoctorQuery = gql`
  query pagedMessagesDr($threadId: ID, $limit: Int, $before: AWSDateTime) {
    getMessagesPaged(threadId: $threadId, limit: $limit, before: $before) {
      __typename
      id
      unreadDoctor
      unreadCustomer
      identityChannel
      channel
      assignedUser
      open
      brand
      hasMore
      customer {
        given
        mobile
        surname
        id
        name
        __typename
      }
      messages {
        __typename
        html
        title
        files
        id
        sent
        createdAt
        senderID
        message
        final
        channel
        photo
        type
        options
      }
    }
  }
`;

const PAGE_SIZE = 30;

export const useCustomerMessages = (ThreadID: string, MarkReadWhenViewed?: boolean) => {
  const messagesEndRef = useRef<any>(null);
  const { isWeb } = useIsMobile();
  const { lookupUser } = useUserMessageNames();
  const channel = useRef<any>();
  const apolloClient = useApolloClient();

  const [allMessages, setAllMessages] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [called, setCalled] = useState(false);
  const [typing, setTyping] = useState(false);
  const [triggeredRead, setTriggeredRead] = useState(false);

  // Used to restore scroll position after prepending older messages.
  const prevScrollHeight = useRef(0);
  const loadingMoreRef = useRef(false);

  // Latest thread metadata (unread flags, channel, customer) from most recent fetch.
  const threadMeta = useRef<any>(null);

  const scrollToBottom = () => {
    if (!messagesEndRef.current) return;
    if (isWeb) {
      messagesEndRef.current.scrollTop = messagesEndRef.current?.scrollHeight;
    } else {
      messagesEndRef.current.scrollToEnd({ animated: true });
    }
  };

  const mergeMessages = (prev: any[], incoming: any[]): any[] => {
    const existingIds = new Set(prev.map((m) => m.id));
    const fresh = incoming.filter((m) => !existingIds.has(m.id));
    if (!fresh.length) return prev;
    return [...prev, ...fresh].sort((a, b) =>
      dayjs(a.sent).isAfter(dayjs(b.sent)) ? 1 : -1,
    );
  };

  const isDoctor = isDoctorPortal();
  const activeQuery = isDoctor ? pagedMessageForDoctorQuery : pagedMessageForCustomerQuery;
  const baseVariables = isDoctor ? { threadId: ThreadID, limit: PAGE_SIZE } : { limit: PAGE_SIZE };

  const { data: queryData, loading, refetch } = useQuery(activeQuery, {
    variables: baseVariables,
    pollInterval: 30 * 1000,
    fetchPolicy: 'network-only',
  });

  // Apollo 3.x onCompleted is unreliable for polls and refetches with network-only.
  // Watch data directly so every network response (initial, poll, refetch) updates state.
  useEffect(() => {
    if (!queryData?.getMessagesPaged) return;
    const incoming = queryData.getMessagesPaged.messages ?? [];
    threadMeta.current = queryData.getMessagesPaged;
    setHasMore(queryData.getMessagesPaged.hasMore ?? false);
    setAllMessages((prev) => mergeMessages(prev, incoming));
    setCalled(true);
  }, [queryData]);

  // After messages render: restore scroll position when prepending older messages,
  // otherwise scroll to bottom (initial load and new incoming messages).
  useLayoutEffect(() => {
    if (prevScrollHeight.current > 0 && messagesEndRef.current && isWeb) {
      const newHeight = messagesEndRef.current.scrollHeight;
      messagesEndRef.current.scrollTop = newHeight - prevScrollHeight.current;
      prevScrollHeight.current = 0;
    } else {
      scrollToBottom();
    }
  }, [allMessages]);

  const loadOlderMessages = async () => {
    if (loadingMoreRef.current || !hasMore || allMessages.length === 0) return;

    const oldest = allMessages[0];
    const before = oldest?.createdAt ?? oldest?.sent;
    if (!before) return;

    if (messagesEndRef.current && isWeb) {
      prevScrollHeight.current = messagesEndRef.current.scrollHeight;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const { data } = await apolloClient.query({
        query: activeQuery,
        variables: { ...baseVariables, before },
        fetchPolicy: 'network-only',
      });

      const older = (data as any)?.getMessagesPaged?.messages ?? [];
      setHasMore((data as any)?.getMessagesPaged?.hasMore ?? false);
      setAllMessages((prev) => mergeMessages(prev, older));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const isDoctorUnread = threadMeta.current?.unreadDoctor ?? false;
  const isCustomerUnread = threadMeta.current?.unreadCustomer ?? false;

  const [readMessagesMutation] = useMutation(gql`
    mutation readMessage($ID: ID) {
      readMessages(threadId: $ID)
    }
  `);

  const channelName = isDoctorPortal() ? `thread-${ThreadID}` : ThreadID;
  const eventName = isDoctorPortal() ? 'thread' : 'message';

  useEffect(() => {
    pusherSubscribeObject(channelName, 'bot_thinking', () => {
      setTyping(true);
    });
  }, [ThreadID, setTyping]);

  useEffect(() => {
    channel.current = pusherSubscribeObject(channelName, eventName, () => {
      if (MarkReadWhenViewed) {
        markAsRead();
      }
      setTyping(false);
      refetch();
    });

    return () => {
      if (channel.current) {
        channel.current.unsubscribe();
      }
    };
  }, [ThreadID, setTyping]);

  const markAsRead = async () => {
    await readMessagesMutation({
      variables: { ID: ThreadID },
      refetchQueries: isDoctorPortal()
        ? [{ query: unreadMessageCountQuery, fetchPolicy: 'network-only' }]
        : [],
      onCompleted: async () => {
        await refetch();
        setTriggeredRead(true);
      },
    });
  };

  useEffect(() => {
    if (MarkReadWhenViewed) {
      markAsRead();
    }
  }, [MarkReadWhenViewed]);

  const onType = () => {
    if ((isDoctorUnread || isCustomerUnread) && !triggeredRead) {
      setTriggeredRead(true);
      markAsRead();
    }
  };

  const transformFrom = (sender: string) => {
    if (sender === ThreadID) return '';
    if (sender === 'platform' && threadMeta.current?.brand === 'leafdoctors')
      return 'Leaf Doctors';
    const userLookedUp = lookupUser(sender);
    if (!userLookedUp) return '';
    return `${userLookedUp?.name} - ${userLookedUp?.title}`;
  };

  const brandImage = () => {
    if (threadMeta.current?.brand === 'heyfella')
      return require('../../Assets/heyfellamark.svg');
    if (threadMeta.current?.brand === 'leafdoctors')
      return require('../../Assets/LeafDoctors/LD_Logo_Mark_Kelp.png');
    return require('../../Assets/onlinedoctor.png');
  };

  const transformSenderImage = (sender: string) => {
    const size = 7;

    if (sender === ThreadID) {
      return (
        <Box w={size} h={size} alignItems={'center'} justifyContent={'center'} bg={'black'} borderRadius={'3xl'}>
          <Icon icon={faUser} color={'white'} size={'md'} />
        </Box>
      );
    }

    if (sender === 'platform') {
      return (
        <Box w={size} h={size}>
          <Image resizeMode={'contain'} source={brandImage()} w={size} h={size} />
        </Box>
      );
    }

    const imageURL = lookupUser(sender)?.image;

    return (
      <Box w={size} h={size}>
        {imageURL && (
          <Image
            borderRadius={'3xl'}
            source={{ uri: imageURL }}
            resizeMode={'contain'}
            w={size}
            h={size}
          />
        )}
        {!imageURL && (
          <Box h={size} w={size} overflow={'hidden'}>
            <Icon icon={faUserCircle} size={'xl'} />
          </Box>
        )}
      </Box>
    );
  };

  const lastMessageIndex = allMessages.length - 1;
  const showMarkAsRead = isDoctorPortal() && isDoctorUnread;

  return {
    channel: threadMeta.current?.channel,
    brand: brandFromCustomerID(ThreadID),
    customerId: threadMeta.current?.customer?.id,
    messages: allMessages,
    hasMore,
    loadingMore,
    loadOlderMessages,
    showMarkAsRead,
    refetch,
    markAsRead,
    transformFrom,
    transformSenderImage,
    scrollToBottom,
    loading: !called && loading,
    lastMessageIndex,
    messagesEndRef,
    onType,
    typing,
  };
};
